// The document-level display attributes the ECONIC theme reads (tokens.css, glass.css, scene.css). WP-THEME.
// public/econic-boot.js sets the first values before the first paint, from the device and from the mirror these setters keep in
// localStorage["econic.display"] (theme choice and ♿ settings; no personal data), plus html.is-safari and lang/dir from "ncp.locale".
// The shell's ♿ menu and preferences (WP-SHELL, pref:ui.theme / pref:ui.display, stored server-side) call these setters, so a returning
// viewer's first paint already has their theme and text size.
//   html[data-theme]        "light" | "dark"                  dark = "ECONIC Night"
//   html[data-look]         "classic" | "signature" | "aurora" the brand look (looks/*.css; 21 Oct 2026), independent of light and dark;
//                           html[data-look-override] while a `?look=` address chose it for this visit (demos, screenshots)
//   html[data-faces]        "icon" | "emblem"                 how the agents are drawn (faces.css; 23 Oct 2026), resolved from
//                           html[data-faces-choice] ("auto" | a style): Automatic is the Icon tile in Classic, the Emblem elsewhere
//   html[data-agent-page]   absent | an agent's key            the agent the screen belongs to (the shell, from the route); its colour
//                           runs through the page in Signature and Aurora (looks/colour.css)
//   html[data-text-size]    absent | "large" | "larger"       leadership presets default to "large"
//   html[data-contrast]     absent | "more"
//   html[data-transparency] absent | "reduce"
//   html[data-motion]       absent | "reduce"
//   html[data-shell]        "econic" while the ECONIC frame or the landing is mounted (set by EconicShell; tokens.css scopes the
//                           names it shares with the NCP theme to it)
import { useSyncExternalStore } from "react";
import { FACE_STYLES, isFaceStyle, type FaceStyle } from "./primitives";

export type ThemeChoice = "light" | "dark" | "system";
/** The brand look (21 Oct 2026, the Ministry's "too plain" review): Classic is the APPLE-SPEC look of 12 Oct, Signature the concept deck's
 *  (serif titles, gold rules, the skyline and ribbons), Aurora the bold one (midnight glows, glass, the logo's O as the AI's orb).
 *  Signature is the default (25 Oct 2026) for anyone who has not chosen one. */
export type Look = "classic" | "signature" | "aurora";
export const LOOKS: readonly Look[] = ["classic", "signature", "aurora"];
export const isLook = (v: unknown): v is Look => v === "classic" || v === "signature" || v === "aurora";
/** The look of anyone who has not chosen one (public/econic-boot.js applies the same before the first paint). */
export const DEFAULT_LOOK: Look = "signature";
/** How the agents are drawn (users, 23 Oct 2026: "agents can have custom images and avatars; maybe something more fun?"): each person picks
 *  the Icon tile or an Emblem; Automatic (unset) follows the look, the tile in Classic and the Emblem in Signature and Aurora. */
export type FaceChoice = "auto" | FaceStyle;
export const FACE_CHOICES: readonly FaceChoice[] = ["auto", ...FACE_STYLES];
export const isFaceChoice = (v: unknown): v is FaceChoice => v === "auto" || isFaceStyle(v);
export const resolveFaces = (choice: FaceChoice, look: Look): FaceStyle => (choice !== "auto" ? choice : look === "classic" ? "icon" : "emblem");
export type DocumentTheme = "light" | "dark";
export type TextSize = "default" | "large" | "larger";

export interface DisplayPrefs {
  textSize?: TextSize;
  contrast?: "default" | "more";
  transparency?: "default" | "reduce";
  motion?: "default" | "reduce";
}

const THEME_COLOR: Record<DocumentTheme, string> = { light: "#012441", dark: "#012441" };
const MIRROR_KEY = "econic.display";
const root = () => (typeof document === "undefined" ? null : document.documentElement);

interface Mirror extends DisplayPrefs {
  theme?: ThemeChoice;
  look?: Look;
  faces?: FaceChoice;
  scene?: string;
}

function remember(patch: Mirror): void {
  try {
    const current = JSON.parse(localStorage.getItem(MIRROR_KEY) ?? "{}") as Mirror;
    localStorage.setItem(MIRROR_KEY, JSON.stringify({ ...current, ...patch }));
  } catch {
    // Private windows and blocked storage: the server-side preference still applies after sign-in.
  }
}

/** Remembers the viewer's scene (pref:ui.scene) for the boot preload of the next visit; SceneBackdrop still takes it as a prop. */
let shellHolders = 0;

/**
 * Holds html[data-shell="econic"] (ECONIC's tokens, faces and radii) while a frame is mounted — the signed-in shell or the sign-in page.
 * Counted, so a page that unmounts after the next one mounted (the sign-in page fading out as the shell arrives) never strips the
 * attribute from under it. Returns the release.
 */
export function holdEconicShell(): () => void {
  const html = document.documentElement;
  shellHolders += 1;
  html.dataset.shell = "econic";
  let released = false;
  return () => {
    if (released) return;
    released = true;
    shellHolders = Math.max(0, shellHolders - 1);
    if (shellHolders === 0) delete html.dataset.shell;
  };
}

export function rememberScene(scene: string | null): void {
  remember({ scene: scene ?? undefined });
}

/** The device's preference, for "system". */
export function systemTheme(): DocumentTheme {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Sets html[data-theme] (and the browser's theme-color) from a choice; returns the theme now in force. */
export function setDocumentTheme(choice: ThemeChoice): DocumentTheme {
  const theme = choice === "system" ? systemTheme() : choice;
  const html = root();
  if (html) {
    html.dataset.theme = theme;
    html.dataset.themeChoice = choice;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
  }
  remember({ theme: choice });
  return theme;
}

/** Sets html[data-look] and remembers it for the next first paint. `override` marks a look a `?look=` address chose (the saved preference
 *  then leaves it alone for the visit); a choice the person makes clears the mark. */
export function setDocumentLook(look: Look, { override = false }: { override?: boolean } = {}): Look {
  const html = root();
  if (html) {
    html.dataset.look = look;
    if (override) html.dataset.lookOverride = "";
    else delete html.dataset.lookOverride;
    // Automatic faces follow the look.
    const choice = html.dataset.facesChoice;
    html.dataset.faces = resolveFaces(isFaceChoice(choice) ? choice : "auto", look);
  }
  remember({ look });
  return look;
}

/** Sets html[data-faces-choice] and the style it resolves to under the current look (html[data-faces]), and remembers the choice for the
 *  next first paint. */
export function setDocumentFaces(choice: FaceChoice): FaceStyle {
  const html = root();
  const look = html?.dataset.look;
  const style = resolveFaces(choice, isLook(look) ? look : DEFAULT_LOOK);
  if (html) {
    html.dataset.facesChoice = choice;
    html.dataset.faces = style;
  }
  remember({ faces: choice });
  return style;
}

/** Names the agent the screen belongs to (html[data-agent-page]; 24 Oct 2026: "more color throughout the UI"): in Signature and Aurora
 *  its colour runs through the whole page (looks/colour.css), its charts and maps included. Null on the screens of no one agent. */
export function setDocumentAgentPage(agent: string | null): void {
  const html = root();
  if (!html) return;
  if (agent) html.dataset.agentPage = agent;
  else delete html.dataset.agentPage;
}

/** Whether a `?look=` address set the look for this visit (public/econic-boot.js). */
export function lookOverridden(): boolean {
  return root()?.dataset.lookOverride !== undefined;
}

/** While the choice is "system", follows the device when it switches between light and dark. Returns the unsubscribe function. */
export function followSystemTheme(): () => void {
  if (typeof matchMedia !== "function") return () => {};
  const query = matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (root()?.dataset.themeChoice === "system") setDocumentTheme("system");
  };
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/** Sets the ♿ display attributes; "default" (or undefined) removes the attribute. */
export function setDocumentDisplay(prefs: DisplayPrefs): void {
  const html = root();
  if (!html) return;
  const set = (name: string, value: string | undefined) => {
    if (!value || value === "default") html.removeAttribute(name);
    else html.setAttribute(name, value);
  };
  if ("textSize" in prefs) set("data-text-size", prefs.textSize);
  if ("contrast" in prefs) set("data-contrast", prefs.contrast);
  if ("transparency" in prefs) set("data-transparency", prefs.transparency);
  if ("motion" in prefs) set("data-motion", prefs.motion);
  remember(prefs);
}

function readTheme(): DocumentTheme {
  return root()?.dataset.theme === "dark" ? "dark" : "light";
}

function subscribe(onChange: () => void): () => void {
  const html = root();
  if (!html || typeof MutationObserver === "undefined") return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(html, { attributes: true, attributeFilter: ["data-theme"] });
  return () => observer.disconnect();
}

/** The theme in force (html[data-theme]), re-rendering when it changes. */
export function useDocumentTheme(): DocumentTheme {
  return useSyncExternalStore(subscribe, readTheme, () => "light");
}

function readLook(): Look {
  const v = root()?.dataset.look;
  return isLook(v) ? v : DEFAULT_LOOK;
}

function subscribeLook(onChange: () => void): () => void {
  const html = root();
  if (!html || typeof MutationObserver === "undefined") return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(html, { attributes: true, attributeFilter: ["data-look"] });
  return () => observer.disconnect();
}

/** The look in force (html[data-look]), re-rendering when it changes: components draw the brand moments (ribbons, the orb, the eyebrow)
 *  only outside Classic, so Classic's markup stays what it was. */
export function useDocumentLook(): Look {
  return useSyncExternalStore(subscribeLook, readLook, () => DEFAULT_LOOK);
}
